<script lang="ts" setup>
import { hasPermission } from '@permission/hasPermission';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import { VButton } from '@shared/component/VButton';
import VChip from '@shared/component/VChip/VChip.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import {
  usePolisAlertsDelete,
  usePolisAlertsGetByRelatie,
} from '@services/alerts-public/orval-api/polis-alerts/polis-alerts';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { tryFormatDate } from '@tools/dates';
import { ref, toRefs, watch } from 'vue';
import { useRouter } from 'vue-router';

import { defaultAlert, getLabelByFrequentie } from '@alert-lib/alertOptions';
import { relatiesGetRelationByRelatienummer } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import PolisAlertForm from './PolisAlertForm.vue';
import { type PolisAlert } from './PolisAlert';

const props = defineProps<{
  adviseurnummer?: number;
  relatienummer: number;
  hideBackButton?: boolean;
}>();

const emit = defineEmits<{
  titleChanged: [value: string];
}>();

const { adviseurnummer, relatienummer } = toRefs(props);

const router = useRouter();
const queryClient = useQueryClient();
const magAanmaken = hasPermission('Alerts.AlertAanmaken');
const magBijwerken = hasPermission('Alerts.AlertBijwerken');
const showAlertForm = ref<boolean>(false);
const selectedAlert = ref<PolisAlert>();
const notification = useNotification();

const { data, isPending, isError, queryKey } = usePolisAlertsGetByRelatie(
  relatienummer,
  { adviseurnummer: adviseurnummer.value },
  {
    query: {
      select: (res) =>
        res.result?.map((a) => ({
          ...a,
          ingangsdatum: a.ingangsdatum ? new Date(a.ingangsdatum) : undefined,
        })) ?? [],
    },
  },
);

const { mutate: remove } = usePolisAlertsDelete({
  mutation: {
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => notification.error('Er is iets fout gegaan bij het verwijderen van de alert.'),
  },
});

function onFormClosed(success: boolean) {
  showAlertForm.value = false;
  if (success) queryClient.invalidateQueries({ queryKey });
}

const { data: relatieData } = useQuery({
  queryKey: ['relatiesGetRelationByRelatienummer', relatienummer.value],
  queryFn: () => relatiesGetRelationByRelatienummer(relatienummer.value),
  select: (e) => e.result,
});

watch(relatieData, (e) => e?.name && emit('titleChanged', `Alerts beheren voor ${e.name}`), {
  immediate: true,
  deep: true,
});

function showForm(alert?: PolisAlert) {
  showAlertForm.value = true;
  selectedAlert.value = alert ?? defaultAlert();
}

function shortenText(input?: string) {
  const maxLength = 130;
  if (input && input.length > maxLength + 2) return `${input.slice(0, maxLength)}...`;
  return input;
}
</script>

<template>
  <div class="mb-6 flex flex-wrap items-center justify-between">
    <VButton
      v-if="!props.hideBackButton"
      prepend-icon="fal fa-arrow-left"
      :inverted="true"
      @click.stop="router.push({ name: 'polisAlertsBeheerOverzicht' })"
    >
      Terug naar overzicht
    </VButton>
  </div>

  <div v-if="isPending">
    <VSkeleton height="lg" :count="4" />
  </div>
  <VInfoBox v-else-if="isError" type="danger">
    Er is een fout opgetreden bij het laden van de polis alerts.
  </VInfoBox>
  <VDataTable
    v-else
    :value="data"
    @row-click="(e: any) => showForm(e.data)"
    sort-field="externPolisnummerPakket"
    :sort-order="1"
    show-row-arrow
    scrollable
    :show-add-button="magAanmaken"
    add-button-label="Alert toevoegen"
    @add-button-clicked="() => showForm()"
  >
    <VColumn field="externPolisnummerPakket" header="Pakketpolis" sortable>
      <template #body="{ data }">{{ data.externPolisnummerPakket }}</template>
    </VColumn>
    <VColumn field="inhoud" header="Inhoud" sortable>
      <template #body="{ data }">
        <span class="text-wrap whitespace-pre">{{ shortenText(data.inhoud) }}</span>
      </template>
    </VColumn>
    <VColumn field="frequentie" header="Frequentie" sortable>
      <template #body="{ data }">{{ getLabelByFrequentie(data.frequentie) }}</template>
    </VColumn>
    <VColumn field="volgendeAlertOp" header="Eerstvolgende verzenddatum" sortable>
      <template #body="{ data }">{{ tryFormatDate(data.volgendeAlertOp) }}</template>
    </VColumn>
    <VColumn field="ontvangers" header="Ontvangers">
      <template #body="{ data }">
        <div class="flex gap-2">
          <VChip :label="ontvanger" v-for="ontvanger in data.ontvangers" :key="ontvanger" />
        </div>
      </template>
    </VColumn>
    <VColumn field="remove" header="">
      <template #body="{ data }">
        <VButton
          type="danger"
          inverted
          confirm-dialog
          confirm-dialog-text="Weet je zeker dat je deze alert wil verwijderen?"
          confirm-dialog-button-text="Verwijderen"
          prepend-icon="far fa-trash-alt"
          :disabled="!magBijwerken"
          @click="() => remove({ polisAlertId: data.id })"
        />
      </template>
    </VColumn>
  </VDataTable>
  <PolisAlertForm
    @formClosed="(e) => onFormClosed(e)"
    :relatienaam="relatieData?.name"
    :alert="selectedAlert"
    :relatienummer="relatienummer"
    :adviseurnummer="adviseurnummer"
    v-if="selectedAlert && showAlertForm"
  />
</template>
